import { API_BASE, api } from '@/lib/api/client';
import type { UserResponse } from '@/lib/api/models';
import { unwrap } from '@/lib/api/request';
import { setAccessToken } from './token';

/** Fetch the current user profile (requires a valid access token). */
export async function fetchMe(): Promise<UserResponse> {
  return unwrap(api.GET('/auth/me'));
}

/** Exchange credentials for an access token (refresh cookie set by the server). */
export async function loginRequest(email: string, password: string): Promise<void> {
  const data = await unwrap(api.POST('/auth/login', { body: { email, password } }));
  setAccessToken(data.access_token);
}

/** End the session on the server (revoke refresh family) and clear local token. */
export async function logoutRequest(): Promise<void> {
  try {
    await api.POST('/auth/logout');
  } finally {
    setAccessToken(null);
  }
}

/**
 * Restore a session on page load via the HTTP-only refresh cookie.
 * Returns the user if a valid session exists, otherwise null.
 */
export async function bootstrapSession(): Promise<UserResponse | null> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { access_token?: string };
  if (!data.access_token) return null;
  setAccessToken(data.access_token);
  try {
    return await fetchMe();
  } catch {
    setAccessToken(null);
    return null;
  }
}
